/**
 * <dd-benefit-tabs> — video state for the Dream Decor benefit-tabs section.
 *
 * Impact's own <x-tabs> already owns selection, ARIA and arrow-key roving, and
 * <video-media> already owns loading, the play button and the `group` attribute
 * that pauses sibling videos when one starts. The ONLY behaviour neither of them
 * provides is pausing a video the viewer has walked away from:
 *
 *   1. switching tabs while a video plays  -> x-tabs hides the panel, the audio
 *      keeps going
 *   2. closing a mobile accordion mid-play -> same
 *   3. scrolling the section off-screen    -> BaseMedia's inView only ever plays,
 *      it never pauses
 *
 * So this element does exactly those three and nothing else. No tab logic, no
 * ARIA: duplicating either would fight the component that already does it well.
 */
if (!window.customElements.get('dd-benefit-tabs')) {
  class DdBenefitTabs extends HTMLElement {
    connectedCallback() {
      // Tab switch. x-tabs pauses no media of its own, and it sets `hidden` only
      // AFTER an await, so watching `selected-index` and pausing everything is
      // both simpler and more correct than trying to read which panel is hidden.
      const tabs = this.querySelector('x-tabs');
      if (tabs) {
        this._tabObserver = new MutationObserver(() => this.pauseWithin(this));
        this._tabObserver.observe(tabs, { attributes: true, attributeFilter: ['selected-index'] });
      }

      // Mobile accordion close. `toggle` does not bubble, so listen in capture.
      this._onToggle = (event) => {
        const details = event.target;
        if (details instanceof HTMLDetailsElement && !details.open) this.pauseWithin(details);
      };
      this.addEventListener('toggle', this._onToggle, true);

      // Scrolled away. threshold 0.2 mirrors the reference implementation.
      this._viewObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) if (!entry.isIntersecting) this.pauseWithin(this);
        },
        { threshold: 0.2 }
      );
      this._viewObserver.observe(this);
    }

    disconnectedCallback() {
      this._tabObserver?.disconnect();
      this._viewObserver?.disconnect();
      this.removeEventListener('toggle', this._onToggle, true);
    }

    /** BaseMedia#pause is a no-op unless the element carries [playing], so this
     *  never instantiates a player for a video nobody has touched. */
    pauseWithin(root) {
      for (const media of root.querySelectorAll('video-media')) {
        if (typeof media.pause === 'function') media.pause();
      }
    }
  }

  window.customElements.define('dd-benefit-tabs', DdBenefitTabs);
}
